import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { DisplayOptions } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { motion } from 'motion/react';
import { UseInfiniteQueryResult } from 'react-query';
import type { PersonTypes } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AppointmentStatusComponents } from '@frontend/appointment-status';
import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import {
  useCalendarEventsConfigurationShallowStore,
  useCalendarEventsFormTypeShallowStore,
  useGetCalendarEventsByAttendeeId,
  useLocationTimezone,
} from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, Chip, ContentLoader, Heading, Tabs, Text, useTooltip } from '@frontend/design-system';
import { useGetAppointmentDetails } from '..';
import { AppointmentTypeChip } from '../components/appointment-type-chip';
import { useAppointmentStatusShallowStore } from '../components/schedule-calendar-components/appointment-event-card/appointmentStatusStore';

const { StatusChip } = AppointmentStatusComponents;

dayjs.extend(utc);
dayjs.extend(minMax);

type ListAppointmentsWidgetProps = {
  person: PersonTypes.Person;
  personLocationId: string;
};

export const ListAppointmentsWidget = ({ person, personLocationId }: ListAppointmentsWidgetProps) => {
  const { t } = useTranslation('schedule');
  const [isOpen, setIsOpen] = useState(false);

  const { selectedLocationIds, selectedGroupId, isSingleTypeScope } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope && selectedLocationIds.length > 1;
  const locationId = isMultiLocation ? selectedGroupId : personLocationId || '';
  const childLocationIds = isMultiLocation ? selectedLocationIds : [];

  const { setSelectedForm, setSelectedPerson } = useCalendarEventsFormTypeShallowStore(
    'setSelectedForm',
    'setSelectedPerson'
  );
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();

  const handleScheduleCalendarAction = (event: SchedulerV3.Types.CalendarEventsEnums) => {
    setSelectedForm(event);
    setSelectedPerson(person);
    setConfiguredCalendarEvent({
      startHour: '',
      endHour: '',
      locationId: '',
      isValid: true,
      calendarDateValue: '',
      calendarEventType: SchedulerV3.Types.CalendarEventsEnums.APPOINTMENT_EVENT,
      name: getFullName(person),
      patientId: person.PersonID,
    });
  };

  const { data: listAppointmentsQuery } = useGetCalendarEventsByAttendeeId(
    person.PersonID,
    locationId,
    childLocationIds
  );

  const allAppointments = useMemo(
    () => listAppointmentsQuery.data?.pages.flatMap((appointment) => appointment.rows) ?? [],
    [listAppointmentsQuery.data?.pages]
  );

  const [pastAppointments, upcomingAppointments] = useMemo(() => {
    const past = [] as Appointment[];
    const upcoming = [] as Appointment[];
    allAppointments.forEach((appointment: Appointment) => {
      // add person to attendeeDetails on the calendar events object
      appointment.person = {
        personId: person.PersonID,
        firstName: person.FirstName,
        lastName: person.LastName,
        preferredName: person.PreferredName,
        mobilePhone: person.MobilePhone,
        homePhone: person.HomePhone,
        status: person.Status as PersonStatus_Enum,
        gender: person.Gender,
        birthDate: person.Birthdate,
        textOptedIn: person.ReceiveSMS,
      };
      const appointmentDate = dayjs(appointment.start);
      if (appointmentDate.isBefore(dayjs(), 'minute')) {
        past.push(appointment);
      } else {
        upcoming.push(appointment);
      }
    });

    return [
      // Sort past by most recent to oldest
      past.sort((a, b) => dayjs(b.start).diff(dayjs(a.start))),
      // Sort upcoming by soonest to furthest in the future
      upcoming.sort((a, b) => dayjs(a.start).diff(dayjs(b.start))),
    ];
  }, [allAppointments, person]);

  const pastInfiniteListQuery = useMemo(() => {
    return {
      ...listAppointmentsQuery,
      data: {
        ...listAppointmentsQuery.data,
        pages: [
          {
            rows: pastAppointments,
          },
        ],
      },
    } as UseInfiniteQueryResult<{ rows: Appointment[] }, unknown>;
  }, [listAppointmentsQuery, pastAppointments]);

  const upcomingInfiniteListQuery = useMemo(() => {
    return {
      ...listAppointmentsQuery,
      data: {
        ...listAppointmentsQuery.data,
        pages: [
          {
            rows: upcomingAppointments,
          },
        ],
      },
    } as UseInfiniteQueryResult<{ rows: Appointment[] }, unknown>;
  }, [listAppointmentsQuery, upcomingAppointments]);

  const recalls = person?.Recall ?? [];

  const dueDate = recalls.map((recall) => {
    return dayjs.utc(recall.EventTime);
  });

  const dueAppointment = recalls.length ? dayjs.min(dueDate).format('MMMM D, YYYY') : null;

  if (!person) return null;

  if (listAppointmentsQuery.isLoading) <ContentLoader />;

  return (
    <>
      <section
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: theme.spacing(2) }}
      >
        <Heading level={3}>{t('Appointments')}</Heading>

        {hasWSM && (
          <Button
            variant='tertiary'
            iconName='plus-small'
            size='small'
            css={{ marginLeft: theme.spacing(1) }}
            onClick={() => {
              handleScheduleCalendarAction(SchedulerV3.Types.CalendarEventsEnums.APPOINTMENT_EVENT);
            }}
          >
            {t('New')}
          </Button>
        )}
      </section>
      {listAppointmentsQuery.isError ? (
        <Text color='error' css={{ textAlign: 'center', paddingBottom: theme.spacing(3) }}>
          {t('Error loading appointments')}
        </Text>
      ) : (
        <>
          <motion.div
            initial={{ height: 0, opacity: 0, padding: 0 }}
            animate={
              !isOpen
                ? { height: 'auto', opacity: 1, padding: theme.spacing(0, 1.5, 2, 1.5) }
                : { height: 0, opacity: 0, padding: 0 }
            }
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            {!!person.Recall.length && dueAppointment && (
              <div
                css={[
                  appointmentItemStyles({ gridTemplateAreas: '"visit date"', isInteractive: false }),
                  { gridTemplateRows: '32px', marginTop: 0, gridTemplateColumns: '90px minmax(90px, 200px)' },
                ]}
              >
                <Chip
                  variant='critical'
                  css={{
                    gridArea: 'visit',
                  }}
                >
                  {'Due Date'}
                </Chip>
                <IconWithText
                  icon='calendar-small'
                  text={dueAppointment}
                  css={{
                    gridArea: 'date',
                  }}
                />
              </div>
            )}
            <>
              {!!allAppointments.length ? (
                <>
                  <AppointmentListItem appointment={pastAppointments[0]} isLast />
                  <AppointmentListItem appointment={upcomingAppointments[0]} isNext />
                </>
              ) : (
                <Text color='light' css={{ textAlign: 'center', padding: theme.spacing(1) }}>
                  {t('No appointments')}
                </Text>
              )}
            </>
          </motion.div>
          <motion.div
            initial={{ height: 0, opacity: 0, padding: 0 }}
            animate={
              isOpen
                ? { height: 'auto', opacity: 1, padding: theme.spacing(0, 1.5, 2, 1.5) }
                : { height: 0, opacity: 0, padding: 0 }
            }
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ pointerEvents: isOpen ? 'auto' : 'none' }} // To prevent overlapping clickable areas
          >
            <Tabs initialTab='past-tab'>
              <Tabs.Bar>
                <Tabs.Tab id='past-tab'>{t('Past')}</Tabs.Tab>
                <Tabs.Tab id='upcoming-tab'>{t('Upcoming')}</Tabs.Tab>
              </Tabs.Bar>

              <Tabs.Panel
                controller='past-tab'
                css={{ maxHeight: 450, overflowY: 'auto', padding: theme.spacing(0, 0.5) }}
              >
                <InfinitePaginatedList
                  height={!!pastAppointments.length || pastInfiniteListQuery.isError ? 300 : 110}
                  infiniteQueryProps={pastInfiniteListQuery}
                  renderListItem={({ listItem }) => {
                    const appointment = listItem as Appointment;
                    if (!appointment) return null;
                    return <AppointmentListItem key={appointment.id} appointment={appointment} />;
                  }}
                  emptyStateConfig={{
                    disableGraphic: true,
                    header: t('No appointments'),
                  }}
                ></InfinitePaginatedList>
              </Tabs.Panel>

              <Tabs.Panel
                controller='upcoming-tab'
                css={{ maxHeight: 450, overflowY: 'auto', padding: theme.spacing(0, 0.5) }}
              >
                <InfinitePaginatedList
                  height={!!upcomingAppointments.length || upcomingInfiniteListQuery.isError ? 300 : 110}
                  infiniteQueryProps={upcomingInfiniteListQuery}
                  renderListItem={({ listItem }) => {
                    const appointment = listItem as Appointment;
                    if (!appointment) return null;
                    return <AppointmentListItem key={appointment.id} appointment={appointment} />;
                  }}
                  emptyStateConfig={{
                    disableGraphic: true,
                    header: t('No appointments'),
                  }}
                ></InfinitePaginatedList>
              </Tabs.Panel>
            </Tabs>
          </motion.div>
          {!!allAppointments.length && (
            <Button
              variant='tertiary'
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: theme.colors.neutral5,
                padding: theme.spacing(1),
                borderBottomLeftRadius: theme.borderRadius.medium,
                borderBottomRightRadius: theme.borderRadius.medium,
                color: theme.colors.neutral60,
              }}
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            >
              {isOpen ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </>
      )}
    </>
  );
};

const IconWithText = ({ icon, text, width }: { icon: IconName; text?: string; width?: number }) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  const [showToolTip, setShowToolTip] = useState(false);

  const measureContentRef = (node: HTMLParagraphElement) => {
    if (!node) return;
    if (node.offsetWidth < node.scrollWidth) {
      setShowToolTip(true);
    } else {
      setShowToolTip(false);
    }
  };

  return (
    <>
      <span
        css={{
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          width: width ?? '100%',
        }}
        {...triggerProps}
      >
        <Icon name={icon} color='subdued' css={{ marginRight: theme.spacing(0.5), minWidth: 16, minHeight: 16 }} />
        <Text
          ref={measureContentRef}
          size='small'
          css={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: width ?? '100%',
          }}
        >
          {!!text ? text : '-'}
        </Text>
      </span>
      {showToolTip && <Tooltip {...tooltipProps}>{text}</Tooltip>}
    </>
  );
};

type AppointmentListItemProps = {
  appointment: Appointment & { displayOptions?: DisplayOptions };
  isNext?: boolean;
  isLast?: boolean;
};

const AppointmentListItem = ({ appointment, isNext, isLast }: AppointmentListItemProps) => {
  const { openModal: handleOpenAppointmentModal, Modal: AppointmentDetailsModal } = useGetAppointmentDetails({
    appointmentId: appointment?.id ?? '',
    locationId: appointment?.locationId ?? '',
    appointments: appointment ? [appointment] : [],
    enabled: !!appointment && !!appointment.id && !!appointment.locationId,
    eventColor: appointment?.displayOptions?.primaryColor,
  });

  // Use stored appointment status for integrated locations since PMS sync may be delayed
  const { appointmentStatus } = useAppointmentStatusShallowStore('appointmentStatus');
  const storedApptStatus = appointmentStatus.find((status) => status?.id === appointment?.id);

  const { locationTimezoneAbbr } = useLocationTimezone(appointment?.locationId ?? '');
  const { isSingleTypeScope, getLocationName } = useAppScopeStore();

  const appointmentTime =
    appointment?.start &&
    `${dayjs(appointment.start).format('h:mm A')} ${
      appointment?.duration ? `- ${dayjs(appointment.start).add(appointment.duration, 'minutes').format('h:mm A')}` : ''
    } ${locationTimezoneAbbr}`.trim();

  const getGridTemplateAreas = ({
    singleTypeScope,
    isNextEvent,
    isLastEvent,
  }: {
    singleTypeScope: boolean;
    isNextEvent?: boolean;
    isLastEvent?: boolean;
  }) => {
    if (isNextEvent || isLastEvent) {
      return singleTypeScope
        ? '"visit visit visit" "date time status" "type provider provider"'
        : '"visit visit visit" "date time status" "location provider provider" "type type type"';
    }
    return singleTypeScope
      ? '"date time status" "type provider provider"'
      : '"date time status" "location provider provider" "type type type"';
  };

  const gridTemplateAreas = getGridTemplateAreas({
    singleTypeScope: isSingleTypeScope,
    isNextEvent: isNext,
    isLastEvent: isLast,
  });

  if (!appointment || !appointment.id) return null;

  return (
    <>
      {AppointmentDetailsModal}
      <div
        css={appointmentItemStyles({ gridTemplateAreas, isInteractive: true, isNext, isLast })}
        onClick={handleOpenAppointmentModal}
      >
        {isNext && (
          <Chip
            css={{
              gridArea: 'visit',
            }}
          >
            {'Next Visit'}
          </Chip>
        )}
        {isLast && (
          <Chip
            variant='eggplant'
            css={{
              gridArea: 'visit',
            }}
          >
            {'Last Visit'}
          </Chip>
        )}

        <IconWithText
          icon='calendar-small'
          text={appointment?.start ? dayjs(appointment?.start).format('MMM D, YYYY') : ''}
          css={{
            gridArea: 'date',
          }}
        />

        <IconWithText
          icon='clock-small'
          text={appointmentTime}
          css={{
            gridArea: 'time',
          }}
        />

        {/* Ensure the status area is always defined */}
        <div
          css={{
            gridArea: 'status',
            justifySelf: 'end',
            minWidth: 24,
          }}
        >
          <StatusChip status={storedApptStatus?.key ?? appointment?.statusOfficeView?.toUpperCase()} isIconOnly />
        </div>

        {appointment?.type ? (
          <AppointmentTypeChip
            name={appointment.type}
            color={appointment?.displayOptions?.primaryColor}
            css={{ gridArea: 'type', maxWidth: isSingleTypeScope ? 90 : '100%' }}
          />
        ) : (
          <Text size='small' color='light' css={{ gridArea: 'type' }}>
            {isSingleTypeScope ? '-' : ''}
          </Text>
        )}

        {appointment?.locationId && !isSingleTypeScope && (
          <IconWithText
            icon='location-small'
            text={getLocationName(appointment?.locationId)}
            css={{
              gridArea: 'location',
            }}
          />
        )}

        <IconWithText
          icon='user-small'
          text={appointment?.practitionerName}
          width={180}
          css={{
            gridArea: 'provider',
          }}
        />
      </div>
    </>
  );
};

type AppointmentItemStylesParams = {
  gridTemplateAreas: string;
  isInteractive?: boolean;
  isNext?: boolean;
  isLast?: boolean;
};

const appointmentItemStyles = ({
  gridTemplateAreas,
  isInteractive = true,
  isNext,
  isLast,
}: AppointmentItemStylesParams) =>
  css({
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.borderRadius.medium,
    border: `1px solid ${theme.colors.neutral5}`,
    boxShadow: theme.shadows.light,
    margin: theme.spacing(1, 0),
    display: 'grid',
    gridTemplateAreas: gridTemplateAreas,
    gridTemplateColumns: '90px minmax(90px, 200px) 32px',
    gridTemplateRows: isNext || isLast ? 'repeat(3, 32px)' : 'repeat(2, 32px)',
    gap: theme.spacing(0.5),
    alignItems: 'center',
    justifyItems: 'start',
    cursor: isInteractive ? 'pointer' : 'default',
    ...(isInteractive && {
      '&:hover': {
        boxShadow: theme.shadows.floating,
        transition: 'all 0.3s ease',
      },
    }),
  });
